import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@/features/auth/AuthContext'
import { fetchControllers } from '@/features/controllers/api'
import {
  createConsolidation,
  deactivateConsolidation,
  deleteConsolidation,
  fetchConsolidationProgress,
  fetchConsolidations,
  reactivateConsolidation,
  reloadConsolidated,
} from '@/features/consolidation/api'
import LoadingState from '@/components/common/LoadingState'
import EmptyState from '@/components/common/EmptyState'
import ErrorState from '@/components/common/ErrorState'

/**
 * Ports includes/consolidation_management.html — merges one controller's
 * historical data into another. All backing JSON endpoints already existed
 * in consolidation.py before this migration; only the controller-picker
 * dropdown needed a new endpoint (GET /portal/controllers/api/v2/controllers).
 */
export default function ConsolidationPage() {
  const { hasPermission } = useAuth()
  const canManage = hasPermission('consolidation.manage')
  const queryClient = useQueryClient()

  const consolidationsQuery = useQuery({ queryKey: ['consolidations'], queryFn: fetchConsolidations })
  const controllersQuery = useQuery({ queryKey: ['controllers'], queryFn: fetchControllers })

  const [primaryId, setPrimaryId] = useState('')
  const [secondaryId, setSecondaryId] = useState('')
  const [reloadingControllerId, setReloadingControllerId] = useState<string | null>(null)

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['consolidations'] })

  const createMutation = useMutation({
    mutationFn: createConsolidation,
    onSuccess: () => {
      setPrimaryId('')
      setSecondaryId('')
      invalidate()
    },
  })

  const deactivateMutation = useMutation({ mutationFn: deactivateConsolidation, onSuccess: invalidate })
  const reactivateMutation = useMutation({ mutationFn: reactivateConsolidation, onSuccess: invalidate })
  const deleteMutation = useMutation({ mutationFn: deleteConsolidation, onSuccess: invalidate })
  const reloadMutation = useMutation({
    mutationFn: reloadConsolidated,
    onSuccess: (_data, controllerId) => setReloadingControllerId(controllerId),
  })

  const progressQuery = useQuery({
    queryKey: ['consolidation-progress', reloadingControllerId],
    queryFn: () => fetchConsolidationProgress(reloadingControllerId!),
    enabled: Boolean(reloadingControllerId),
    refetchInterval: (query) => {
      const status = query.state.data?.status
      return status === 'complete' || status === 'error' ? false : 2000
    },
  })

  if (consolidationsQuery.isLoading) return <LoadingState label="Loading consolidations…" />
  if (consolidationsQuery.isError) {
    return (
      <ErrorState
        message={consolidationsQuery.error instanceof Error ? consolidationsQuery.error.message : 'Failed to load.'}
        onRetry={() => consolidationsQuery.refetch()}
      />
    )
  }

  const consolidations = consolidationsQuery.data ?? []
  const controllers = controllersQuery.data ?? []

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-ink">Consolidation</h1>

      {canManage && (
        <section className="rounded-2xl border border-nav-line bg-card p-5">
          <h2 className="mb-3 text-base font-semibold text-ink">Create Consolidation</h2>
          <form
            onSubmit={(e) => {
              e.preventDefault()
              if (primaryId && secondaryId && primaryId !== secondaryId) {
                createMutation.mutate({ primary_controller_id: primaryId, secondary_controller_id: secondaryId })
              }
            }}
            className="flex flex-wrap items-end gap-3"
          >
            <div className="max-w-full">
              <label className="mb-1.5 block text-xs font-semibold text-nav-muted">Primary Controller</label>
              <select
                value={primaryId}
                onChange={(e) => setPrimaryId(e.target.value)}
                className="max-w-full rounded-xl border border-nav-line bg-input px-3.5 py-2.5 text-sm text-text-primary focus:border-brand focus:outline-none"
              >
                <option value="">Select…</option>
                {controllers.map((c) => (
                  <option key={c.controller_id} value={c.controller_id}>
                    {c.controller_name || c.controller_id}
                  </option>
                ))}
              </select>
            </div>
            <div className="max-w-full">
              <label className="mb-1.5 block text-xs font-semibold text-nav-muted">Secondary Controller</label>
              <select
                value={secondaryId}
                onChange={(e) => setSecondaryId(e.target.value)}
                className="max-w-full rounded-xl border border-nav-line bg-input px-3.5 py-2.5 text-sm text-text-primary focus:border-brand focus:outline-none"
              >
                <option value="">Select…</option>
                {controllers.map((c) => (
                  <option key={c.controller_id} value={c.controller_id}>
                    {c.controller_name || c.controller_id}
                  </option>
                ))}
              </select>
            </div>
            <button
              type="submit"
              disabled={createMutation.isPending || !primaryId || !secondaryId || primaryId === secondaryId}
              className="rounded-full bg-brand px-5 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
            >
              {createMutation.isPending ? 'Creating…' : 'Create Consolidation'}
            </button>
          </form>
          {createMutation.isError && (
            <p className="mt-2 text-sm text-error">
              {createMutation.error instanceof Error ? createMutation.error.message : 'Failed to create.'}
            </p>
          )}
        </section>
      )}

      {reloadingControllerId && progressQuery.data && (
        <section className="rounded-2xl border border-brand bg-card p-5">
          <h2 className="mb-2 text-sm font-semibold text-text-primary">
            Reloading {reloadingControllerId} — {progressQuery.data.stage}
          </h2>
          <div className="h-2 w-full overflow-hidden rounded-full bg-bg-secondary">
            <div
              className="h-full bg-brand transition-all"
              style={{ width: `${progressQuery.data.progress}%` }}
            />
          </div>
          <p className="mt-1 text-xs text-text-secondary">{progressQuery.data.message}</p>
          {(progressQuery.data.status === 'complete' || progressQuery.data.status === 'error') && (
            <button
              type="button"
              onClick={() => setReloadingControllerId(null)}
              className="mt-2 text-xs text-accent hover:underline"
            >
              Dismiss
            </button>
          )}
        </section>
      )}

      <section>
        {consolidations.length === 0 ? (
          <EmptyState message="No consolidations yet." />
        ) : (
          <div className="overflow-x-auto rounded-2xl border border-nav-line bg-card">
            <table className="w-full text-[13.5px]">
              <thead>
                <tr className="border-b border-nav-line text-left text-xs text-nav-muted">
                  <th className="px-4 py-3.5 font-semibold">Primary</th>
                  <th className="px-4 py-3.5 font-semibold">Secondary</th>
                  <th className="px-4 py-3.5 font-semibold">Status</th>
                  {canManage && <th className="px-4 py-3.5 font-semibold">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {consolidations.map((c) => (
                  <tr key={c.id} className="border-b border-nav-line last:border-0">
                    <td className="px-4 py-3.5 text-ink">{c.primary_controller_name || c.primary_controller_id}</td>
                    <td className="px-4 py-3.5 text-ink">
                      {c.secondary_controller_name || c.secondary_controller_id}
                    </td>
                    <td className="px-4 py-3.5 text-ink">{c.is_active ? 'Active' : 'Inactive'}</td>
                    {canManage && (
                      <td className="px-4 py-3.5">
                        <div className="flex flex-wrap gap-2">
                          {c.is_active ? (
                            <>
                              <button
                                type="button"
                                onClick={() => reloadMutation.mutate(c.primary_controller_id)}
                                className="text-xs text-accent hover:underline"
                              >
                                Reload Data
                              </button>
                              <button
                                type="button"
                                onClick={() => deactivateMutation.mutate(c.id)}
                                className="text-xs text-text-secondary hover:underline"
                              >
                                Deactivate
                              </button>
                            </>
                          ) : (
                            <button
                              type="button"
                              onClick={() => reactivateMutation.mutate(c.id)}
                              className="text-xs text-brand hover:underline"
                            >
                              Reactivate
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => {
                              if (confirm('Delete this consolidation?')) deleteMutation.mutate(c.id)
                            }}
                            className="text-xs text-error hover:underline"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  )
}
